import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Users, Check, Truck, Lock, Heart, Banknote } from "lucide-react";
import TopBar from "@/components/TopBar";
import CashPaymentInstructions from "@/components/CashPaymentInstructions";
import { ReservationSuccessModal } from "@/components/CollectiveGiftModal";
import StatusBadge from "@/components/StatusBadge";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { categoryLabel } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

const FALLBACK_IMG =
  "https://images.pexels.com/photos/5872179/pexels-photo-5872179.jpeg?auto=compress&cs=tinysrgb&w=900";

const PAYMENT_OPTIONS = [
  { id: "mpesa", labelKey: "pay.mpesa", color: "#E60000" },
  { id: "orange", labelKey: "pay.orange", color: "#FF7900" },
  { id: "airtel", labelKey: "pay.airtel", color: "#E40520" },
  { id: "illicocash", labelKey: "pay.illicocash", color: "#0066B3" },
  { id: "card", labelKey: "pay.card", color: "#2A0E3C" },
];

// Cash is offered only for individual full purchases (not cagnottes / last-minute).
const CASH_OPTION = { id: "cash", labelKey: "pay.cash", color: "#3FA535" };

const emptyForm = {
  contributor_name: "",
  contributor_phone: "",
  contributor_email: "",
  payment_method: "mpesa",
  message: "",
  is_anonymous: false,
};

export default function ProductDetail() {
  const { code, productId } = useParams();
  const [params] = useSearchParams();
  const collectiveMode = params.get("collective") === "1";
  const joinParam = params.get("join");
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [mainImage, setMainImage] = useState("");

  // Buy in full drawer
  const [buyDrawerOpen, setBuyDrawerOpen] = useState(false);
  const [buyForm, setBuyForm] = useState(emptyForm);
  const [buySubmitting, setBuySubmitting] = useState(false);
  const [buyError, setBuyError] = useState("");
  const [success, setSuccess] = useState(null);

  // Join group pot drawer
  const [joinDrawerOpen, setJoinDrawerOpen] = useState(false);
  const [groupCode, setGroupCode] = useState(joinParam ? joinParam.toUpperCase() : "");
  const [joinForm, setJoinForm] = useState(emptyForm);
  const [joinSubmitting, setJoinSubmitting] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [joinSuccess, setJoinSuccess] = useState(null);

  // Settle-remaining drawer (one-shot payment that closes the cagnotte)
  const [settleDrawerOpen, setSettleDrawerOpen] = useState(false);
  const [settleForm, setSettleForm] = useState(emptyForm);
  const [settleSubmitting, setSettleSubmitting] = useState(false);
  const [settleError, setSettleError] = useState("");
  const [settleSuccess, setSettleSuccess] = useState(null);

  // Collective reservation (creator path)
  const [reservationResult, setReservationResult] = useState(null);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [reservationError, setReservationError] = useState("");

  // Cagnotte code gate (Q6): a guest without the reservation code cannot see
  // any group details and cannot act on the product. This modal asks for the
  // code before either the buy-full (settle) or join-pot drawer opens.
  const [codeGateOpen, setCodeGateOpen] = useState(false);
  const [codeGateAction, setCodeGateAction] = useState(null); // "buy" | "join" | "settle"
  const [codeGateInput, setCodeGateInput] = useState("");
  const [codeGateError, setCodeGateError] = useState("");
  const [codeGateBusy, setCodeGateBusy] = useState(false);

  // Define the loader inline inside the effect so the dependency array
  // captures `productId` and `code` (no stale closure on remount).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ event_code: code });
        if (joinParam) qs.set("join_code", joinParam);
        const { data } = await api.get(`/public/products/${productId}?${qs.toString()}`);
        if (!cancelled) {
          setProduct(data);
          setMainImage(data.image_url || FALLBACK_IMG);
        }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail));
      }
    })();
    return () => { cancelled = true; };
  }, [productId, code, joinParam]);

  const load = async () => {
    try {
      const qs = new URLSearchParams({ event_code: code });
      if (joinParam) qs.set("join_code", joinParam);
      const { data } = await api.get(`/public/products/${productId}?${qs.toString()}`);
      setProduct(data);
      setMainImage(data.image_url || FALLBACK_IMG);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  // (Removed) — Previous build auto-opened the join drawer on `?join=CODE`.
  // The new flow lets the guest see the full cagnotte card first, with the
  // group, representative, collected/target/remaining details, and then pick
  // between "Participer à la cagnotte" or "Solder le montant restant".

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF9FB]">
        <TopBar />
        <div className="max-w-md mx-auto px-5 mt-20 text-center">
          <p className="text-[#D81B8C] font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FFF9FB] flex items-center justify-center text-[#5C4664]">
        {t("common.loading")}
      </div>
    );
  }

  const status = product.status || "ouvert";
  const isUnique = product.is_unique;
  const offeringCount = product.offering_count || 0;
  const galleryImages = [product.image_url, ...(product.additional_images || [])].filter(Boolean);
  if (galleryImages.length === 0) galleryImages.push(FALLBACK_IMG);
  const isLocked = isUnique && status === "offert";
  const activeReservation = product.active_reservation;
  // When the guest arrives without a valid `?join=CODE`, the backend masks
  // group details and returns `{has_active_cagnotte: true, code_required: true}`.
  // In that case: hide identifying info AND gate the CTAs behind a code prompt.
  const cagnotteCodeRequired = !!(activeReservation && activeReservation.code_required);

  const openCodeGate = (action) => {
    setCodeGateAction(action);
    setCodeGateInput("");
    setCodeGateError("");
    setCodeGateOpen(true);
  };

  const submitCodeGate = async (e) => {
    e?.preventDefault?.();
    const entered = codeGateInput.toUpperCase().replace(/[^A-Z0-9]/g, "").trim();
    if (!entered) {
      setCodeGateError("Code requis.");
      return;
    }
    setCodeGateBusy(true);
    setCodeGateError("");
    try {
      const qs = new URLSearchParams({ event_code: code, join_code: entered });
      const { data } = await api.get(`/public/products/${productId}?${qs.toString()}`);
      const stillMasked = !!(data.active_reservation && data.active_reservation.code_required);
      if (stillMasked) {
        setCodeGateError("Code invalide. Vérifiez auprès de l'organisateur du groupe.");
        return;
      }
      // Success: propagate the code through the URL so the rest of the flow
      // (join drawer, settle drawer) can pick it up via ?join=CODE.
      const action = codeGateAction;
      setCodeGateOpen(false);
      setGroupCode(entered);
      navigate(`/event/${code}/gift/${productId}?join=${entered}`, { replace: true });
      // Open the appropriate drawer once the URL flip has settled.
      setTimeout(() => {
        if (action === "buy") setBuyDrawerOpen(true);
        else if (action === "settle") setSettleDrawerOpen(true);
        else setJoinDrawerOpen(true);
      }, 60);
    } catch (err) {
      setCodeGateError(formatApiError(err.response?.data?.detail) || "Code invalide.");
    } finally {
      setCodeGateBusy(false);
    }
  };
  // Purchase freeze: normal products → frozen 48h before event;
  // special products → frozen at J+1 (24h after event start).
  const isSpecial = !!product.is_special;
  const purchaseFrozen = isSpecial
    ? !!product.event_special_frozen
    : !!product.event_frozen;

  // Reserve product for the group (creator flow, after CollectiveGiftModal stored data)
  const reserveCollective = async () => {
    setReservationError("");
    const raw = sessionStorage.getItem("milkado_collective");
    if (!raw) {
      setReservationError("Données de groupe manquantes. Recommencez depuis le bouton « Cadeau collectif ».");
      return;
    }
    const groupData = JSON.parse(raw);
    setReservationLoading(true);
    try {
      const { data } = await api.post("/public/reservations", {
        product_id: product.id,
        event_code: code,
        group_name: groupData.group_name,
        creator_name: groupData.creator_name,
        creator_phone: groupData.creator_phone,
        participants_count: groupData.participants_count,
      });
      sessionStorage.removeItem("milkado_collective");
      setReservationResult(data);
      load();
    } catch (e) {
      setReservationError(formatApiError(e.response?.data?.detail));
    } finally {
      setReservationLoading(false);
    }
  };

  // Buy in full submit
  const submitBuy = async (e) => {
    e.preventDefault();
    setBuyError("");
    if (!buyForm.contributor_name.trim()) {
      setBuyError(t("product.mustEnterName"));
      return;
    }
    setBuySubmitting(true);
    try {
      const { data } = await api.post("/public/contributions", {
        product_id: product.id,
        event_code: code,
        ...buyForm,
        // SEC-002: amount + is_full_purchase are now derived server-side.
        // We only send an `intent` hint; the backend computes the real amount
        // from the product/reservation state.
        intent: "buy_full",
      });
      setSuccess(data);
      setBuyDrawerOpen(false);
      setBuyForm(emptyForm);
      load();
    } catch (e) {
      setBuyError(formatApiError(e.response?.data?.detail));
    } finally {
      setBuySubmitting(false);
    }
  };

  // Join group pot submit
  const submitJoin = async (e) => {
    e.preventDefault();
    setJoinError("");
    const normCode = groupCode.toUpperCase().replace(/[^A-Z0-9]/g, "").trim();
    if (normCode.length < 4) {
      setJoinError(t("product.mustEnterCode"));
      return;
    }
    if (!joinForm.contributor_name.trim()) {
      setJoinError(t("product.mustEnterName"));
      return;
    }
    setJoinSubmitting(true);
    try {
      const { data } = await api.post(`/public/reservations/${normCode}/join`, joinForm);
      setJoinSuccess(data);
      setJoinDrawerOpen(false);
      setJoinForm(emptyForm);
      setGroupCode("");
      load();
    } catch (e) {
      setJoinError(formatApiError(e.response?.data?.detail));
    } finally {
      setJoinSubmitting(false);
    }
  };

  // Settle remaining: one-shot payment that closes the cagnotte.
  const submitSettle = async (e) => {
    e.preventDefault();
    setSettleError("");
    if (!groupCode.trim()) {
      setSettleError("Code de groupe manquant.");
      return;
    }
    if (!settleForm.contributor_name.trim()) {
      setSettleError(t("product.mustEnterName"));
      return;
    }
    setSettleSubmitting(true);
    try {
      const normCode = groupCode.toUpperCase().replace(/[^A-Z0-9]/g, "").trim();
      const { data } = await api.post(
        `/public/reservations/${normCode}/settle`,
        settleForm,
      );
      setSettleSuccess(data);
      setSettleDrawerOpen(false);
      setSettleForm(emptyForm);
      load();
    } catch (e) {
      setSettleError(formatApiError(e.response?.data?.detail));
    } finally {
      setSettleSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-32">
      <TopBar />
      <div className="max-w-5xl mx-auto px-5">
        <button
          onClick={() =>
            navigate(collectiveMode ? `/event/${code}/shop?collective=1` : -1)
          }
          data-testid="product-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("product.backToShop")}
        </button>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Gallery */}
          <div>
            <div className="aspect-square w-full rounded-3xl overflow-hidden bg-[#FCE7F3] border border-[#F2E2EC]">
              <img src={mainImage} alt={product.name} className="w-full h-full object-cover" />
            </div>
            {galleryImages.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {galleryImages.map((img, i) => (
                  <button
                    key={img}
                    onClick={() => setMainImage(img)}
                    data-testid={`thumb-${i}`}
                    className={`aspect-square rounded-xl overflow-hidden border-2 ${
                      mainImage === img ? "border-[#D81B8C]" : "border-transparent"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#D81B8C]">
                {categoryLabel(product.category, lang)}
              </span>
              <StatusBadge status={status} />
              {isUnique && (
                <span data-testid="unique-tag" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-[#6B2C8F]">
                  <Lock className="w-3 h-3" /> Unique
                </span>
              )}
            </div>
            <h1 data-testid="product-name" className="mt-2 font-display text-xl sm:text-2xl font-black text-[#2A0E3C] leading-snug">
              {product.name}
            </h1>
            {product.description && (
              <p className="mt-2 text-sm text-[#5C4664] leading-relaxed">{product.description}</p>
            )}

            {product.supplier_name && (
              <div className="mt-3 flex items-center gap-2 text-xs text-[#5C4664]">
                <Truck className="w-3.5 h-3.5 text-[#6B2C8F]" />
                <span>{t("product.supplier")} :</span>
                <span className="font-semibold text-[#2A0E3C]">{product.supplier_name}</span>
              </div>
            )}

            <div className="mt-5 p-4 rounded-2xl bg-white border border-[#F2E2EC]">
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#5C4664]">
                  {t("product.priceFor")}
                </span>
                <div>
                  <span className="font-display text-2xl font-black text-[#D81B8C]">
                    ${(product.final_price ?? product.price).toFixed(0)}
                  </span>
                  <div className="text-[10px] text-[#5C4664] text-right">
                    {t("common2.allInclusive")}
                  </div>
                </div>
              </div>

              {/* Home-delivery fee breakdown (shown when the event has
                  home_delivery=true so the guest sees WHY the price is
                  higher). The fee is already baked into final_price above. */}
              {product.home_delivery_applied && product.delivery_fee > 0 && (
                <div
                  data-testid="delivery-fee-line"
                  className="mt-3 pt-3 border-t border-dashed border-[#F2E2EC] text-[11px] text-[#5C4664] flex items-center justify-between"
                >
                  <span className="flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5 text-[#6B2C8F]" />
                    <span>Dont livraison à domicile</span>
                  </span>
                  <span className="font-semibold text-[#2A0E3C]">
                    ${product.delivery_fee.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Offering count notice */}
              {offeringCount > 0 && !isLocked && (
                <div
                  data-testid="offering-count"
                  className="mt-3 p-3 rounded-xl bg-[#E8F6E5] flex items-start gap-2"
                >
                  <Heart className="w-3.5 h-3.5 text-[#3FA535] mt-0.5 shrink-0" />
                  <div className="text-xs text-[#206F18]">
                    {t("product.offeredAlreadyBy")}{" "}
                    <strong>
                      {offeringCount} {offeringCount > 1 ? t("product.persons") : t("product.person")}
                    </strong>
                    .{" "}
                    <span className="text-[#5C4664]">{t("product.stillOfferable")}</span>
                  </div>
                </div>
              )}

              {/* Active reservation info */}
              {activeReservation && cagnotteCodeRequired && (
                <div
                  data-testid="reservation-info-masked"
                  className="mt-3 p-4 rounded-xl bg-[#EFE3F5] text-xs text-[#2A0E3C]"
                >
                  <div className="font-display text-base font-black text-[#6B2C8F] flex items-center gap-1.5">
                    <Lock className="w-4 h-4" /> Ce cadeau a été réservé pour une cagnotte.
                  </div>
                  <p className="mt-1 text-[#5C4664]">
                    Vous devez fournir le <strong>code de la cagnotte</strong> pour participer
                    ou solder le montant restant. Ce code est partagé par l&apos;organisateur du groupe.
                  </p>
                </div>
              )}
              {activeReservation && !cagnotteCodeRequired && (
                <div
                  data-testid="reservation-info"
                  className={`mt-3 p-4 rounded-xl text-xs text-[#2A0E3C] ${
                    joinParam ? "bg-[#F5EBFA] border-2 border-[#6B2C8F]" : "bg-[#EFE3F5]"
                  }`}
                >
                  <div className="font-display text-base font-black text-[#6B2C8F] flex items-center gap-1.5">
                    <Users className="w-4 h-4" /> {activeReservation.group_name}
                  </div>
                  {(activeReservation.representative_name || activeReservation.creator_name) && (
                    <div className="mt-1 text-[#5C4664]">
                      Représentant :{" "}
                      <strong className="text-[#2A0E3C]">
                        {activeReservation.representative_name || activeReservation.creator_name}
                      </strong>
                      {(activeReservation.representative_phone || activeReservation.creator_phone) ? (
                        <span className="text-[#5C4664]"> · {activeReservation.representative_phone || activeReservation.creator_phone}</span>
                      ) : null}
                    </div>
                  )}
                  <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                    <div className="bg-white/60 rounded-lg px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-widest text-[#5C4664]">Collecté</div>
                      <div data-testid="cagnotte-collected" className="font-display text-sm font-black text-[#3FA535]">
                        ${(activeReservation.collected_amount ?? 0).toFixed(0)}
                      </div>
                    </div>
                    <div className="bg-white/60 rounded-lg px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-widest text-[#5C4664]">Objectif</div>
                      <div data-testid="cagnotte-target" className="font-display text-sm font-black text-[#2A0E3C]">
                        ${(activeReservation.target_amount ?? 0).toFixed(0)}
                      </div>
                    </div>
                    <div className="bg-white/60 rounded-lg px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-widest text-[#5C4664]">Reste</div>
                      <div data-testid="cagnotte-remaining" className="font-display text-sm font-black text-[#D81B8C]">
                        ${(activeReservation.remaining_amount ?? 0).toFixed(0)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-[#5C4664]">
                    {activeReservation.paid_participants_count}/
                    {activeReservation.participants_count}{" "}
                    {t("product.participantsProgress")} · {t("product.yourShare")} :{" "}
                    <strong className="text-[#2A0E3C]">
                      ${activeReservation.share_amount?.toFixed(2)}
                    </strong>
                  </div>
                </div>
              )}
            </div>

            {/* CTAs */}
            <div className="mt-5 space-y-3">
              {isLocked ? (
                <p
                  data-testid="product-unique-locked"
                  className="p-4 rounded-xl bg-[#EFE3F5] text-[#6B2C8F] text-sm font-medium flex items-start gap-2"
                >
                  <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                  {t("product.uniqueLocked")}
                </p>
              ) : purchaseFrozen ? (
                <p
                  data-testid="product-purchase-frozen"
                  className="p-4 rounded-xl bg-[#FCE7F3] text-[#A60F6E] text-sm font-medium flex items-start gap-2"
                >
                  <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                  {t("event.frozenBody")}
                </p>
              ) : collectiveMode ? (
                <>
                  <button
                    onClick={reserveCollective}
                    disabled={reservationLoading || status === "reserve"}
                    data-testid="reserve-btn"
                    className="w-full p-4 rounded-2xl bg-[#3FA535] hover:bg-[#2E7E27] text-white transition-colors flex items-center justify-between gap-4 disabled:opacity-60"
                  >
                    <div className="text-left">
                      <div className="font-display text-base font-bold flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {t("collective.reserveBtn")}
                      </div>
                      <div className="text-xs text-white/80">
                        {t("collective.successSub")}
                      </div>
                    </div>
                    <span className="font-display text-lg font-black">${(product.final_price ?? product.price).toFixed(0)}</span>
                  </button>
                  {reservationError && (
                    <p data-testid="reservation-error" className="text-sm text-[#D81B8C] font-medium">
                      {reservationError}
                    </p>
                  )}
                </>
              ) : (
                <>
                  {joinParam && activeReservation ? (
                    <>
                      <button
                        onClick={() => setJoinDrawerOpen(true)}
                        data-testid="join-pot-btn"
                        className="w-full text-left p-4 rounded-2xl bg-[#3FA535] text-white hover:bg-[#2E7E27] transition-colors flex items-center justify-between gap-4"
                      >
                        <div>
                          <div className="font-display text-base font-bold flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Participer à la cagnotte
                          </div>
                          <div className="text-xs text-white/80">
                            Payez votre part — ${activeReservation.share_amount?.toFixed(2)}
                          </div>
                        </div>
                        <span className="font-display text-lg font-black">
                          ${activeReservation.share_amount?.toFixed(0)}
                        </span>
                      </button>
                      <button
                        onClick={() => setSettleDrawerOpen(true)}
                        data-testid="settle-remaining-btn"
                        className="w-full text-left p-4 rounded-2xl bg-white border-2 border-[#D81B8C] text-[#2A0E3C] hover:bg-[#FCE7F3] transition-colors flex items-center justify-between gap-4"
                      >
                        <div>
                          <div className="font-display text-base font-bold flex items-center gap-2 text-[#D81B8C]">
                            <Check className="w-4 h-4" />
                            Solder le montant restant
                          </div>
                          <div className="text-xs text-[#5C4664]">
                            Payez tout ce qu&apos;il manque et clôturez la cagnotte
                          </div>
                        </div>
                        <span className="font-display text-lg font-black text-[#D81B8C]">
                          ${(activeReservation.remaining_amount ?? 0).toFixed(0)}
                        </span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          if (cagnotteCodeRequired) openCodeGate("buy");
                          else setBuyDrawerOpen(true);
                        }}
                        data-testid="buy-full-btn"
                        className="w-full text-left p-4 rounded-2xl bg-[#2A0E3C] text-white hover:bg-black transition-colors flex items-center justify-between gap-4"
                      >
                        <div>
                          <div className="font-display text-base font-bold flex items-center gap-2">
                            {cagnotteCodeRequired && <Lock className="w-4 h-4" />}
                            {t("product.buyFull")}
                          </div>
                          <div className="text-xs text-white/70">
                            {cagnotteCodeRequired ? "Code de la cagnotte requis" : t("product.buyFullSub")}
                          </div>
                        </div>
                        <span className="font-display text-lg font-black">${(product.final_price ?? product.price).toFixed(0)}</span>
                      </button>

                      {activeReservation && (
                        <button
                          onClick={() => {
                            if (cagnotteCodeRequired) openCodeGate("join");
                            else setJoinDrawerOpen(true);
                          }}
                          data-testid="join-pot-btn"
                          className="w-full text-left p-4 rounded-2xl bg-white border-2 border-[#3FA535] text-[#2A0E3C] hover:bg-[#E8F6E5] transition-colors flex items-center justify-between gap-4"
                        >
                          <div>
                            <div className="font-display text-base font-bold flex items-center gap-2">
                              <Users className="w-4 h-4 text-[#3FA535]" />
                              {t("product.joinPot")}
                            </div>
                            <div className="text-xs text-[#5C4664]">{t("product.joinPotSub")}</div>
                          </div>
                          <span className="font-display text-base font-black text-[#3FA535]">
                            ${activeReservation.share_amount?.toFixed(0)}
                          </span>
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Buy in full drawer */}
      <Drawer open={buyDrawerOpen} onOpenChange={setBuyDrawerOpen}>
        <DrawerContent className="bg-white max-h-[92vh]">
          <div className="max-w-2xl mx-auto w-full px-5 pb-8 overflow-y-auto">
            <DrawerHeader className="px-0">
              <DrawerTitle className="font-display text-2xl font-black text-[#2A0E3C]">
                {t("product.buyFull")}
              </DrawerTitle>
              <DrawerDescription className="text-[#5C4664]">
                {product.name} — ${(product.final_price ?? product.price).toFixed(0)}
              </DrawerDescription>
            </DrawerHeader>

            <PaymentForm
              form={buyForm}
              setForm={setBuyForm}
              error={buyError}
              submitting={buySubmitting}
              onSubmit={submitBuy}
              allowCash
            />
          </div>
        </DrawerContent>
      </Drawer>

      {/* Join group pot drawer */}
      <Drawer open={joinDrawerOpen} onOpenChange={setJoinDrawerOpen}>
        <DrawerContent className="bg-white max-h-[92vh]">
          <div className="max-w-2xl mx-auto w-full px-5 pb-8 overflow-y-auto">
            <DrawerHeader className="px-0">
              <DrawerTitle className="font-display text-2xl font-black text-[#2A0E3C]">
                {t("product.joinPot")}
              </DrawerTitle>
              <DrawerDescription className="text-[#5C4664]">
                {t("product.groupCagnotteFor")} {product.name}
              </DrawerDescription>
            </DrawerHeader>

            <form onSubmit={submitJoin} className="space-y-5">
              <Field label={t("product.groupCode")}>
                <Input
                  data-testid="join-code-input"
                  value={groupCode}
                  onChange={(e) =>
                    setGroupCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4))
                  }
                  placeholder="ABCD"
                  maxLength={4}
                  className="h-12 text-center font-mono text-2xl font-bold tracking-[0.4em] rounded-xl border-[#F2E2EC] focus-visible:ring-[#3FA535]"
                />
              </Field>

              {activeReservation && (
                <div className="p-3 rounded-xl bg-[#E8F6E5] text-xs text-[#206F18]">
                  {t("product.yourShare")}:{" "}
                  <strong>${activeReservation.share_amount?.toFixed(2)}</strong>{" "}
                  ({activeReservation.participants_count} {t("collective.participantsCount").toLowerCase()})
                </div>
              )}

              <PaymentFormFields form={joinForm} setForm={setJoinForm} t={t} />

              {joinError && (
                <p data-testid="join-error" className="text-sm text-[#D81B8C] font-medium">
                  {joinError}
                </p>
              )}

              <Button
                type="submit"
                disabled={joinSubmitting}
                data-testid="join-submit-btn"
                className="w-full rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-white h-14 font-semibold text-base"
              >
                {joinSubmitting ? t("common.loading") : t("product.payNow")}
              </Button>
            </form>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Settle-remaining drawer */}
      <Drawer open={settleDrawerOpen} onOpenChange={setSettleDrawerOpen}>
        <DrawerContent className="bg-white max-h-[92vh]">
          <div className="max-w-2xl mx-auto w-full px-5 pb-8 overflow-y-auto">
            <DrawerHeader className="px-0">
              <DrawerTitle className="font-display text-2xl font-black text-[#2A0E3C]">
                Solder le montant restant
              </DrawerTitle>
              <DrawerDescription className="text-[#5C4664]">
                {activeReservation?.group_name} —{" "}
                <strong className="text-[#D81B8C]">
                  ${(activeReservation?.remaining_amount ?? 0).toFixed(2)}
                </strong>{" "}
                à payer pour clôturer la cagnotte.
              </DrawerDescription>
            </DrawerHeader>

            <form onSubmit={submitSettle} className="space-y-5">
              <div className="p-3 rounded-xl bg-[#FCE7F3] text-xs text-[#A60F6E]">
                Vous payez le solde complet (${(activeReservation?.remaining_amount ?? 0).toFixed(2)}).
                La cagnotte sera <strong>automatiquement clôturée</strong> et le cadeau passera
                en pipeline logistique.
              </div>
              <PaymentFormFields form={settleForm} setForm={setSettleForm} t={t} />
              {settleError && (
                <p data-testid="settle-error" className="text-sm text-[#D81B8C] font-medium">
                  {settleError}
                </p>
              )}
              <Button
                type="submit"
                disabled={settleSubmitting}
                data-testid="settle-submit-btn"
                className="w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-14 font-semibold text-base"
              >
                {settleSubmitting
                  ? t("common.loading")
                  : `Solder $${(activeReservation?.remaining_amount ?? 0).toFixed(2)}`}
              </Button>
            </form>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Success drawer (covers buy, join, and settle) */}
      <Drawer
        open={!!success || !!joinSuccess || !!settleSuccess}
        onOpenChange={(o) => {
          if (!o) {
            setSuccess(null);
            setJoinSuccess(null);
            setSettleSuccess(null);
          }
        }}
      >
        <DrawerContent className="bg-white">
          <div className="max-w-md mx-auto w-full px-6 pb-10 pt-2 text-left">
            {success?.cash_code ? (
              <CashPaymentInstructions
                contribution={success}
                eventCode={code}
                productName={product?.name}
                onClose={() => {
                  setSuccess(null);
                  navigate(`/event/${code}`);
                }}
              />
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#3FA535] text-white flex items-center justify-center">
                  <Check className="w-7 h-7" />
                </div>
                <h2 data-testid="success-title" className="mt-5 font-display text-3xl font-black text-[#2A0E3C]">
                  {t("product.thankYouTitle")}
                </h2>
                <p className="mt-2 text-[#5C4664]">{t("product.thankYouSub")}</p>
                {(success || joinSuccess || settleSuccess)?.transaction_ref && (
                  <p className="mt-5 text-xs text-[#5C4664]">
                    {t("product.transactionRef")}:{" "}
                    <span className="font-mono font-semibold text-[#2A0E3C]">
                      {(success || joinSuccess || settleSuccess).transaction_ref}
                    </span>
                  </p>
                )}
                <Button
                  onClick={() => {
                    setSuccess(null);
                    setJoinSuccess(null);
                    setSettleSuccess(null);
                    navigate(`/event/${code}`);
                  }}
                  data-testid="success-back-btn"
                  className="mt-6 w-full rounded-full bg-[#2A0E3C] hover:bg-black text-white h-12 font-semibold"
                >
                  {t("product.backToEvent")}
                </Button>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Reservation success modal (for collective creator) */}
      <ReservationSuccessModal
        reservation={reservationResult}
        open={!!reservationResult}
        onOpenChange={(o) => {
          if (!o) {
            setReservationResult(null);
            navigate(`/event/${code}`);
          }
        }}
      />

      {/* Cagnotte code gate (Q6): asks for the reservation code before opening
          either the buy-full or join-pot drawer. */}
      <Dialog open={codeGateOpen} onOpenChange={(v) => { if (!v) setCodeGateOpen(false); }}>
        <DialogContent data-testid="cagnotte-code-gate" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-[#2A0E3C] flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#6B2C8F]" />
              Code de la cagnotte requis
            </DialogTitle>
            <DialogDescription className="text-[#5C4664]">
              Ce cadeau est réservé pour une cagnotte. Saisissez le code partagé par
              l&apos;organisateur du groupe pour continuer.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCodeGate} className="space-y-3">
            <Input
              value={codeGateInput}
              onChange={(e) => {
                setCodeGateInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4));
                setCodeGateError("");
              }}
              placeholder="Ex: TY27"
              autoFocus
              autoCapitalize="characters"
              className="uppercase font-display text-lg tracking-widest text-center"
              data-testid="cagnotte-code-gate-input"
            />
            {codeGateError && (
              <p className="text-sm text-red-600 font-medium" data-testid="cagnotte-code-gate-error">
                {codeGateError}
              </p>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCodeGateOpen(false)}
                className="rounded-full"
                data-testid="cagnotte-code-gate-cancel"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={codeGateBusy}
                className="rounded-full bg-[#D81B8C] hover:bg-[#B21571] text-white disabled:opacity-60"
                data-testid="cagnotte-code-gate-submit"
              >
                {codeGateBusy ? "Vérification…" : "Continuer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentForm({ form, setForm, error, submitting, onSubmit, allowCash }) {
  const { t } = useLanguage();
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <PaymentFormFields form={form} setForm={setForm} t={t} allowCash={allowCash} />
      {error && (
        <p data-testid="buy-error" className="text-sm text-[#D81B8C] font-medium">{error}</p>
      )}
      <Button
        type="submit"
        disabled={submitting}
        data-testid="pay-now-btn"
        className="w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-14 font-semibold text-base"
      >
        {submitting ? t("common.loading") : t("product.payNow")}
      </Button>
    </form>
  );
}

function PaymentFormFields({ form, setForm, t, allowCash = false }) {
  const options = allowCash ? [...PAYMENT_OPTIONS, CASH_OPTION] : PAYMENT_OPTIONS;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("product.yourName")}>
          <Input
            data-testid="contributor-name-input"
            value={form.contributor_name}
            onChange={(e) => setForm({ ...form, contributor_name: e.target.value })}
            className="h-12 rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
          />
        </Field>
        <Field label={t("product.yourPhone")}>
          <Input
            data-testid="contributor-phone-input"
            value={form.contributor_phone}
            onChange={(e) => setForm({ ...form, contributor_phone: e.target.value })}
            placeholder="+243…"
            className="h-12 rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
          />
        </Field>
      </div>

      <Field label={t("product.message")}>
        <Textarea
          data-testid="message-input"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          rows={2}
          className="rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
        />
      </Field>

      <label className="flex items-center gap-3 cursor-pointer">
        <Checkbox
          data-testid="anonymous-checkbox"
          checked={form.is_anonymous}
          onCheckedChange={(v) => setForm({ ...form, is_anonymous: !!v })}
          className="border-[#F2E2EC] data-[state=checked]:bg-[#D81B8C] data-[state=checked]:border-[#D81B8C]"
        />
        <span className="text-sm text-[#2A0E3C]">{t("product.anonymous")}</span>
      </label>

      <div>
        <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-2 block">
          {t("product.paymentMethod")}
        </Label>
        <RadioGroup
          value={form.payment_method}
          onValueChange={(v) => setForm({ ...form, payment_method: v })}
          className="grid grid-cols-1 sm:grid-cols-2 gap-2"
        >
          {options.map((p) => (
            <label
              key={p.id}
              htmlFor={`pay-${p.id}`}
              data-testid={`pay-option-${p.id}`}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                form.payment_method === p.id
                  ? "border-[#D81B8C] bg-[#FCE7F3]"
                  : "border-[#F2E2EC] hover:border-[#2A0E3C]/30"
              }`}
            >
              <RadioGroupItem value={p.id} id={`pay-${p.id}`} className="border-[#5C4664] text-[#D81B8C]" />
              {p.id === "cash" ? (
                <Banknote className="w-4 h-4" style={{ color: p.color }} />
              ) : (
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
              )}
              <span className="text-sm font-medium text-[#2A0E3C]">{t(p.labelKey)}</span>
            </label>
          ))}
        </RadioGroup>
        {form.payment_method === "cash" ? (
          <p data-testid="cash-helper" className="mt-3 text-xs text-[#3FA535] bg-[#E8F6E5] border border-[#3FA535]/30 rounded-xl p-2.5 leading-snug">
            ✦ Vous recevrez un code de paiement à 5 caractères. Vous aurez ensuite 72h pour payer
            soit au bureau Milkado, soit par dépôt bancaire (mention « Cadeau XXXXX »). Les
            équipes Finance valideront la réception avant la prise en charge logistique.
          </p>
        ) : (
          <p className="mt-3 text-xs text-[#5C4664] italic">{t("pay.sandboxNote")}</p>
        )}
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
